import { onINP } from "web-vitals/attribution";
import type { INPMetricWithAttribution } from "web-vitals/attribution";
import Bugsnag from "@bugsnag/js";

/**
 * Field INP telemetry: reports slow interactions to Bugsnag with the attribution needed to name
 * the culprit — the input-delay / processing / presentation split, and the Long Animation Frame
 * scripts that ran inside the interaction (a `setTimeout` invoker points at deferred work like the
 * island snapshot; a long click listener with no forced layout points at handler cost).
 *
 * Only interactions the page can do something about are reported: those where scripts LoAF
 * attributes to the page, rather than to a browser extension, run for at least half the
 * interaction. Slow keystrokes on iOS, a menu animating on a weak GPU, or a password manager
 * holding the thread all fail that test and stay out of Bugsnag. So does an interaction whose
 * cost is style, layout and paint the page triggered without scripting it: script time is the
 * one signal that tells our work from the device's, and rendering-bound interactions are the
 * price of keeping the rest quiet. Browsers without LoAF (Safari, Firefox) never report. The
 * frame-level LoAF timings, the kind of key pressed, whether a form submitted inside the
 * interaction, and whether the target was autofilled then split what is reported between
 * handler cost and the rendering the handlers forced.
 *
 * Reports fire mid-session on the first interaction crossing the threshold (`reportAllChanges`),
 * not at page hide: Bugsnag delivers over XHR, which a closing tab — or a closing Chromebook
 * lid — would drop. One report per page view keeps the volume one order below pageviews.
 *
 * Reports group per route and interaction type, so opening a popup and typing into it on the
 * same page are separate groups with separate culprits.
 */

const REPORT_THRESHOLD_MS = 500;
/** The share of the interaction that page scripts must explain for a report to be worth reading. */
const ACTIONABLE_SCRIPT_SHARE = 0.5;
const EXTENSION_SCHEMES = [
  "chrome-extension:",
  "moz-extension:",
  "safari-web-extension:",
  "ms-browser-extension:",
];
const MAX_LOAF_SCRIPTS = 5;
const MAX_LOAF_FRAMES = 3;

/**
 * How many objects the island on this page holds, if it holds one — the INP culprits on game
 * pages all scale with it. Counted passively from the events the game already dispatches.
 */
let islandObjectCount: number | null = null;
let reported = false;

type KeyKind = "Enter" | "Tab" | "Backspace" | "Escape" | "character" | "other";

/** The most recent key pressed, by kind only — never the character, which may be a password. */
let lastKey: { kind: KeyKind; time: DOMHighResTimeStamp } | null = null;
let lastSubmitTime: DOMHighResTimeStamp | null = null;

function trackIslandObjectCount(): void {
  window.addEventListener(
    "world-objects-load",
    (event: CustomEvent<{ worldObjects: unknown[] }>) => {
      islandObjectCount = event.detail.worldObjects.length;
    },
  );
  window.addEventListener("world-object-added", () => {
    islandObjectCount = (islandObjectCount ?? 0) + 1;
  });
  window.addEventListener("world-object-removed", () => {
    if (islandObjectCount !== null && islandObjectCount > 0) {
      islandObjectCount -= 1;
    }
  });
}

/**
 * A keydown can arrive with no `key` at all: password managers, IME composition and some
 * extensions dispatch them, so a missing key is read as "other" rather than a crash.
 */
function keyKind(event: KeyboardEvent): KeyKind {
  const key: unknown = event.key;
  if (typeof key !== "string") {
    return "other";
  }
  switch (key) {
    case "Enter":
    case "Tab":
    case "Backspace":
    case "Escape":
      return key;
    default:
      return key.length === 1 ? "character" : "other";
  }
}

/** Capture-phase listeners so a handler that stops propagation does not hide the interaction. */
function trackKeyboardAndSubmit(): void {
  window.addEventListener(
    "keydown",
    (event) => {
      lastKey = { kind: keyKind(event), time: event.timeStamp };
    },
    { capture: true, passive: true },
  );
  window.addEventListener(
    "submit",
    (event) => {
      lastSubmitTime = event.timeStamp;
    },
    { capture: true, passive: true },
  );
}

/** The pathname with numeric segments collapsed, so reports group per page type, not per entity. */
function normalizedRoute(): string {
  return window.location.pathname.replace(/\d+/g, ":n");
}

function loafScripts(
  metric: INPMetricWithAttribution,
): PerformanceScriptTiming[] {
  return metric.attribution.longAnimationFrameEntries.flatMap(
    (entry) => entry.scripts,
  );
}

function isExtensionScript(script: PerformanceScriptTiming): boolean {
  return EXTENSION_SCHEMES.some((scheme) =>
    script.sourceURL.startsWith(scheme),
  );
}

/**
 * How long a script ran inside the interaction. An overlapping LoAF frame can start before the
 * interaction and carry earlier work, so only the overlap counts; without both interaction
 * timestamps there is nothing to clip against and the whole script counts.
 */
function durationWithinInteraction(
  metric: INPMetricWithAttribution,
  script: PerformanceScriptTiming,
): number {
  const { interactionTime, nextPaintTime } = metric.attribution;
  if (interactionTime == null || nextPaintTime == null) {
    return script.duration;
  }
  const start = Math.max(script.startTime, interactionTime);
  const end = Math.min(script.startTime + script.duration, nextPaintTime);
  return Math.max(0, end - start);
}

/**
 * Whether the page's own scripts explain enough of the interaction to act on. Any source that is
 * not an extension is the page's, including Bugsnag's, which wraps our listeners and so takes
 * the attribution for their time.
 */
function explainedByPageScripts(metric: INPMetricWithAttribution): boolean {
  const scripted = loafScripts(metric)
    .filter((script) => !isExtensionScript(script))
    .reduce(
      (total, script) => total + durationWithinInteraction(metric, script),
      0,
    );
  return scripted >= metric.value * ACTIONABLE_SCRIPT_SHARE;
}

function loafScriptSummary(
  metric: INPMetricWithAttribution,
): Record<string, string | number>[] {
  return loafScripts(metric)
    .sort((a, b) => b.duration - a.duration)
    .slice(0, MAX_LOAF_SCRIPTS)
    .map((script) => ({
      invoker: script.invoker,
      invokerType: script.invokerType,
      sourceURL: script.sourceURL,
      sourceFunctionName: script.sourceFunctionName,
      duration: Math.round(script.duration),
      forcedStyleAndLayoutDuration: Math.round(
        script.forcedStyleAndLayoutDuration,
      ),
    }));
}

/**
 * Frame-level LoAF timings, relative to the frame start. Present even when the frame ran no
 * attributable script, which is what separates rendering cost from time spent outside the page.
 */
function loafFrameSummary(
  metric: INPMetricWithAttribution,
): Record<string, number>[] {
  return metric.attribution.longAnimationFrameEntries
    .slice()
    .sort((a, b) => b.duration - a.duration)
    .slice(0, MAX_LOAF_FRAMES)
    .map((frame) => ({
      duration: Math.round(frame.duration),
      blockingDuration: Math.round(frame.blockingDuration),
      renderStart: Math.round(frame.renderStart - frame.startTime),
      styleAndLayoutStart: Math.round(
        frame.styleAndLayoutStart - frame.startTime,
      ),
      firstUIEvent: Math.round(frame.firstUIEventTimestamp - frame.startTime),
      scriptCount: frame.scripts.length,
    }));
}

/** Whether a timestamp falls inside the interaction, from its first event to the next paint. */
function withinInteraction(
  metric: INPMetricWithAttribution,
  time: DOMHighResTimeStamp | null | undefined,
): boolean {
  const { interactionTime, nextPaintTime } = metric.attribution;
  if (time == null || interactionTime == null || nextPaintTime == null) {
    return false;
  }
  return time >= interactionTime && time <= nextPaintTime;
}

/**
 * Whether the interaction target is a browser-autofilled field. `:autofill` throws where
 * unsupported, and the selector web-vitals builds may not resolve once the DOM has moved on.
 */
function targetAutofilled(selector: string | undefined): boolean | null {
  if (!selector) {
    return null;
  }
  try {
    const target = document.querySelector(selector);
    return target ? target.matches(":autofill") : null;
  } catch {
    return null;
  }
}

function report(metric: INPMetricWithAttribution): void {
  if (
    reported ||
    metric.value < REPORT_THRESHOLD_MS ||
    !explainedByPageScripts(metric)
  ) {
    return;
  }
  reported = true;

  const route = normalizedRoute();
  const attribution = metric.attribution;
  const interactionType = attribution.interactionType ?? "unknown";
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;

  Bugsnag.notify(
    new Error(
      `INP ${Math.round(metric.value)}ms — ${interactionType} on ${route}`,
    ),
    (event) => {
      event.severity = "info";
      event.groupingHash = `inp:${route}:${interactionType}`;
      event.context = route;
      event.addMetadata("inp", {
        value: Math.round(metric.value),
        rating: metric.rating,
        interactionType,
        interactionTarget: attribution.interactionTarget,
        inputDelay: Math.round(attribution.inputDelay),
        processingDuration: Math.round(attribution.processingDuration),
        presentationDelay: Math.round(attribution.presentationDelay),
        loadState: attribution.loadState,
        eventTypes: attribution.processedEventEntries.map(
          (entry) => entry.name,
        ),
        keyKind: withinInteraction(metric, lastKey?.time) ? lastKey.kind : null,
        submitDuringInteraction: withinInteraction(metric, lastSubmitTime),
        targetAutofilled: targetAutofilled(attribution.interactionTarget),
        loafScripts: loafScriptSummary(metric),
        loafFrames: loafFrameSummary(metric),
      });
      event.addMetadata("inpContext", {
        route,
        islandObjectCount,
        deviceMemory: (navigator as Navigator & { deviceMemory?: number })
          .deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        effectiveConnectionType: connection?.effectiveType,
        saveData: connection?.saveData,
      });
    },
  );
}

/** Wire after InitBugsnag, once per page. */
export default function InitInpReporting(): void {
  trackIslandObjectCount();
  trackKeyboardAndSubmit();
  onINP(report, { reportAllChanges: true });
}
